import { __, sprintf } from '@wordpress/i18n';
import { Checkout, TaxZones } from '../types';

export interface TaxLine {
  label: string | null;
  rate: number;
  amount: number;
  display_amount: string;
  taxable_display_amount?: string;
}

/**
 * Tax rows to render for a checkout. Prefers the per-rate breakdown so stacked
 * taxes (e.g. GST + PST) get one row each; falls back to the aggregate scalars
 * for platform responses that predate `tax_breakdown`.
 */
export const getTaxLines = (checkout: Checkout): TaxLine[] => {
  const breakdown = (checkout?.tax_breakdown || []).filter(item => !!item?.tax_amount);

  if (breakdown.length) {
    return breakdown.map(item => ({
      label: item.tax_label ?? checkout?.tax_label,
      rate: item.tax_rate,
      amount: item.tax_amount,
      display_amount: item.tax_display_amount,
      taxable_display_amount: item.taxable_display_amount,
    }));
  }

  if (!checkout?.tax_amount) {
    return [];
  }

  return [
    {
      label: checkout.tax_label,
      rate: checkout.tax_percent,
      amount: checkout.tax_amount,
      display_amount: checkout.tax_display_amount,
    },
  ];
};

/** Rate detail for a tax row, matching the platform receipt: "(5% on $739.00)". */
export const formatTaxLineDetail = (line: TaxLine): string => {
  if (!line.taxable_display_amount) {
    return `(${line.rate}%)`;
  }
  /* translators: 1: tax rate, 2: amount the rate was applied to */
  return `(${sprintf(__('%1$s%% on %2$s', 'surecart'), line.rate, line.taxable_display_amount)})`;
};
export const zones: TaxZones = {
  ca_gst: {
    label: __('GST Number', 'surecart'),
    label_small: __('CA GST', 'surecart'),
  },
  au_abn: {
    label: __('ABN Number', 'surecart'),
    label_small: __('AU ABN', 'surecart'),
  },
  gb_vat: {
    label: __('VAT Number', 'surecart'),
    label_small: __('UK VAT', 'surecart'),
  },
  eu_vat: {
    label: __('VAT Number', 'surecart'),
    label_small: __('EU VAT', 'surecart'),
  },
  other: {
    label: __('Tax ID', 'surecart'),
    label_small: __('Other', 'surecart'),
  },
};

export const getType = key => {
  key = key ? key.toUpperCase() : key;
  if (key === 'CA') {
    return 'ca_gst';
  }
  if (key === 'AU') {
    return 'au_abn';
  }
  if (key === 'GB') {
    return 'gb_vat';
  }
  if (
    ['AT', 'BE', 'EU', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'EL', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'RO', 'SK', 'SI', 'ES', 'SE'].includes(key)
  ) {
    return 'eu_vat';
  }
  return null;
};

export const formatTaxDisplay = (taxLabel, estimated = false) => {
  const label = estimated ? __('Estimated Tax', 'surecart') : __('Tax', 'surecart');
  return !taxLabel ? label : `${label}: ${taxLabel}`;
};
